import { useEffect, useState } from "react";
import { getIdToken } from "./auth.js";

export type SessionState = "idle" | "creating" | "ready" | "denied" | "error";

/**
 * Exchange the signed-in user's Entra ID token for the mktg_auth session
 * cookie (POST /auth/session). The cookie — not the token — is what
 * authorizes the manifest fetch and /cdn images (browsers can't put Bearer
 * headers on <img> tags), so the library must not load until this is "ready".
 */
export function useSession(active: boolean): { state: SessionState; error: string | null } {
  const [state, setState] = useState<SessionState>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!active) {
      setState("idle");
      return;
    }
    let cancelled = false;
    setState("creating");
    setError(null);
    // Returns "denied" (401/403 — not authorized), throws on transient error.
    const mint = async (): Promise<"ok" | "denied"> => {
      const idToken = await getIdToken();
      const res = await fetch("/auth/session", {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (res.status === 401 || res.status === 403) return "denied";
      if (!res.ok) throw new Error(`session request failed (${res.status})`);
      return "ok";
    };
    void (async () => {
      try {
        const r = await mint();
        if (!cancelled) setState(r === "denied" ? "denied" : "ready");
      } catch (e) {
        if (!cancelled) {
          setError((e as Error).message);
          setState("error");
        }
      }
    })();
    // The cookie expires after 12h; a tab left open would silently start
    // 401ing images. Re-mint well inside that window (MSAL silently refreshes
    // the Entra token). Failures here are non-fatal — retried next interval.
    const renew = setInterval(() => {
      void mint().then(
        (r) => { if (!cancelled && r === "denied") setState("denied"); },
        () => undefined,
      );
    }, 4 * 60 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(renew);
    };
  }, [active]);

  return { state, error };
}
